import { unmarshal as defaultUmarshal } from '@ogury/unmarshaller';
import { builder } from './builder';
import { constants, units } from './constants';
import { recommendedLinksConfig } from './recommended-links';
import { contentDiscoveryConfig } from './content-discovery';
import { getLocale } from '../translator';

export { builder, constants };

export function displayTrackersConfig() {
  return builder.holder({
    trackers: builder.string('display_trackers', {
      defaultValue: '',
      description: 'tracks to inject in the format'
    }),
    omidTrackers: builder.string('omid_trackers', {
      defaultValue: '',
      description:
        'omid trackers to inject with display trackers when OMID is deactivated'
    }),
    omidTrackingEnabled: builder.boolean('omid_tracking_enabled', {
      defaultValue: false,
      description: 'true if OMID tracking is activated'
    }),
    allowAgnosticTracking: builder.boolean('allow_display_agnostic_tracking', {
      defaultValue: false,
      description:
        'true if can inject OMID trackers with display trackers when OMID is deactivated'
    }),
    inhouse: builder.boolean('display_tracker_ogury', {
      defaultValue: false,
      description: 'enable ogury display tracker (send events in ES)'
    }),
    htmlContainerSelector: builder.string('dt_force_html_container', {
      defaultValue: '',
      description:
        'put a css selector to inject html Display Trackers in a different element'
    }),
    scriptContainerSelector: builder.string('dt_force_script_container', {
      defaultValue: '',
      description:
        'put a css selector to inject html Display Trackers in a different element'
    }),
    overloadDocumentWrite: builder.boolean('dt_overload_document_write', {
      defaultValue: true,
      description:
        'if false, disable the overload document.write that may be used in a display tracker'
    }),
    enableSentryLog: builder.boolean('dt_enable_sentry_log', {
      defaultValue: true,
      description: 'enable sentry log sended in document.write overload'
    })
  });
}

export function flattenConfig(unmarshaller: Record<string, any>): any[] {
  const keys = Object.keys(unmarshaller);

  return keys.reduce((acc: any[], k) => {
    const config = unmarshaller[k];

    if (config.type === 'holder' || config.type === 'or') {
      return [...acc, ...flattenConfig(config.children)];
    }

    acc.push(config);

    return acc;
  }, []);
}

export function extractAvailableParams(
  unmarshaller: Record<string, any>
): string[] {
  return flattenConfig(unmarshaller).map(e => e.name);
}

export const defaults = {
  watermarkApiKeys: builder.csv('watermark_api_keys', {
    defaultValue: ['apikey-watermark'],
    delimiter: ',',
    description: 'Apikeys of apps to show the watermark'
  }),
  watermarkPosition: builder.string('watermark_position', {
    of: ['bottom-left', 'bottom-right'],
    defaultValue: 'bottom-left',
    description: 'The position of the watermark on the screen.'
  }),
  browser: builder.string('browser', {
    of: ['browser', 'newbrowser', 'defaultbrowser', 'chrometab'],
    defaultValue: 'newbrowser',
    label: 'Browser',
    description:
      'browser launched after a click. Ogury Browser is only available for in-app ad formats.'
  }),
  displayTrackers: displayTrackersConfig(),
  interaction: builder.string('interaction', {
    of: ['touch', 'click'],
    defaultValue: 'touch',
    label: 'Interaction',
    description: 'Interaction of the format'
  }),
  locale: builder.string('locale', {
    label: 'Locale',
    description: 'locale of the format',
    defaultValue: (getLocale() as string) || 'en',
    of: [
      'auto',
      'ar',
      'bg',
      'cs',
      'da',
      'de',
      'el',
      'en',
      'es',
      'et',
      'fi',
      'fr',
      'ga',
      'hr',
      'hu',
      'it',
      'ja',
      'ko',
      'lt',
      'lv',
      'mt',
      'nb',
      'nl',
      'pl',
      'pt',
      'ro',
      'ru',
      'sk',
      'sl',
      'sv'
    ]
  }),
  sdkVersion: builder.string('sdk_version', {
    label: 'SDK version',
    description: 'sdk version used by the publisher',
    optional: true
  }),
  odid: builder.string('odid', {
    label: 'User odid',
    description: 'user odid',
    optional: true
  }),
  application: builder.object('application', {
    description: 'info related to the publisher application',
    defaultValue: {}
  }),
  recommendedLinks: recommendedLinksConfig,
  contentDiscovery: contentDiscoveryConfig,
  omidTrackers: builder.string('omid_trackers', {
    defaultValue: '',
    description: 'OMID tracks to inject in the format'
  }),
  omidTrackingEnabled: builder.boolean('omid_tracking_enabled', {
    defaultValue: false
  }),
  omidImpressionDelay: builder.number('omid_impression_delay', {
    defaultValue: 250,
    unit: units.milliseconds
  }),
  closeBtnOffsetY: builder.number('close_btn_offset_y', {
    defaultValue: 0,
    description: 'Offset vertical in px from the top of the screen',
    unit: units.pixel
  })
};

export function unmarshal(ad: any, unmarshaller: any): any {
  return defaultUmarshal(ad.getParam.bind(ad), unmarshaller);
}
